/**
 * LocationForm
 *
 * Collects zip code with validation and clear button; reports validity
 * via onRepsLoaded.
 */
import React, { useState, useEffect } from 'react';
import { Label } from '../../../components/ui/inputs/label';
import { Input } from '../../../components/ui/inputs/input';
import { X } from 'lucide-react';

export function LocationForm({ 
  formData, 
  handleInputChange, 
  theme, 
  themes,
  customTheme,
  getTextStyle,
  getSubtextStyle,
  onRepsLoaded = () => {}
}) {
  const [error, setError] = useState(null);
  const isFormValid = formData.zipCode?.match(/^\d{5}$/);

  // Add debugging to see what theme is being passed
  useEffect(() => {
    console.log('LocationForm theme debug:', { theme, themes: Object.keys(themes || {}) });
  }, [theme, themes]);

  // Automatically set repsLoaded to true when zip code is valid
  useEffect(() => {
    if (typeof onRepsLoaded === 'function') {
      if (isFormValid) {
        onRepsLoaded(true);
      } else {
        onRepsLoaded(false);
      }
    }
  }, [formData.zipCode, isFormValid, onRepsLoaded]);

  const handleClearZipCode = () => {
    handleInputChange({ target: { name: 'zipCode', value: '' } });
    setError(null);
    if (typeof onRepsLoaded === 'function') {
      onRepsLoaded(false);
    }
  };

  return (
    <div>
      <h2 className={`text-2xl font-bold ${
        theme 
          ? theme === 'custom' 
              ? 'text-white' 
              : themes[theme]?.text || 'text-gray-900'
          : 'text-gray-900'
      }`} style={theme === 'custom' && getTextStyle ? getTextStyle() : {}}>
        Location
      </h2>
      <p className={`mt-2 text-sm ${
        theme 
          ? theme === 'custom' 
              ? 'text-white/80' 
              : themes[theme]?.subtext || 'text-gray-600'
          : 'text-gray-600'
      }`} style={theme === 'custom' && getSubtextStyle ? getSubtextStyle() : {}}>
        Please provide your zip code to help us understand our community better.
      </p>

      <div className="mt-8">
        <div className="flex gap-4">
          <div className="flex-1">
            <Label 
              htmlFor="zipCode" 
              className={
                theme 
                  ? theme === 'custom' 
                      ? 'text-white' 
                      : themes[theme]?.text || 'text-gray-900'
                  : 'text-gray-900'
              }
              style={theme === 'custom' && getTextStyle ? getTextStyle() : {}}
            >
              Zip code
            </Label>
            <Input
              id="zipCode"
              name="zipCode"
              value={formData.zipCode}
              onChange={handleInputChange}
              maxLength={5}
              pattern="[0-9]*"
              inputMode="numeric"
              required
              className={`mt-2 ${
                theme 
                  ? theme === 'custom' 
                    ? '!border-white/20 !bg-white/20 !text-white placeholder:text-white/50 focus:ring-white/20' 
                    : `!border-white/20 !bg-white/20 !text-white placeholder:text-white/50 focus:ring-white/20`
                  : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-500'
              }`}
              style={theme === 'custom' && getTextStyle ? getTextStyle() : theme ? { 
                color: 'white !important',
                backgroundColor: 'rgba(255, 255, 255, 0.2) !important',
                borderColor: 'rgba(255, 255, 255, 0.2) !important'
              } : {}}
            />
          </div>
          <div className="flex items-end">
            {formData.zipCode && (
              <button
                onClick={handleClearZipCode}
                className={`mb-[2px] p-2.5 rounded-lg ${
                  theme 
                    ? theme === 'custom'
                        ? 'border-2 border-white/20 text-white hover:bg-white/10' 
                        : `${themes[theme]?.border || 'border-white/20'} border-2 ${themes[theme]?.text || 'text-white'} hover:bg-white/10` 
                    : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className={`mt-4 rounded-lg border p-4 ${
            theme 
              ? theme === 'custom'
                ? 'border-white/20 bg-black/20'
                : `${themes[theme]?.border || 'border-white/20'} bg-black/20` 
              : 'border-red-200 bg-red-50'
          }`}>
            <div className="flex items-start">
              <div className={`mr-3 flex-shrink-0 ${
                theme 
                  ? theme === 'custom'
                    ? 'text-white'
                    : themes[theme]?.text || 'text-white' 
                  : 'text-red-600'
              }`} style={theme === 'custom' && getTextStyle ? getTextStyle() : {}}>
                <X className="h-5 w-5" />
              </div>
              <div>
                <h3 className={`text-sm font-medium ${
                  theme 
                    ? theme === 'custom'
                      ? 'text-white'
                      : themes[theme]?.text || 'text-white' 
                    : 'text-red-800'
                }`} style={theme === 'custom' && getTextStyle ? getTextStyle() : {}}>
                  Error
                </h3>
                <div className={`mt-2 text-sm ${
                  theme 
                    ? theme === 'custom'
                      ? 'text-white/80'
                      : themes[theme]?.subtext || 'text-white/80' 
                    : 'text-red-700'
                }`} style={theme === 'custom' && getSubtextStyle ? getSubtextStyle() : {}}>
                  {error}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Location information disclaimer */}
        <div className={`text-xs ${
          theme 
            ? theme === 'custom'
              ? 'text-white/80' 
              : themes[theme]?.subtext || 'text-white/80'
            : 'text-gray-500'
        } opacity-80 mt-6 p-3 rounded-lg ${
          theme 
            ? theme === 'custom'
              ? 'border border-white/10 bg-white/5'
              : 'border border-white/10 bg-white/5'
            : 'border border-gray-200 bg-gray-50'
        }`} style={theme === 'custom' && getSubtextStyle ? getSubtextStyle() : {}}>
          Your zip code helps us understand the geographic distribution of our survey responses. This allows us to properly analyze feedback from different regions and communities.
        </div>
      </div>
    </div>
  );
}